import type { CatalogPermission, CatalogRole, PanelRole, RoleAssignment } from '@citibox-panel/api-client';
import { isAccessManagement, isKnownPermission, type Permission } from '@/lib/access';

// What the dialog edits: the managed role ids and the extras, exactly as they will be sent.
export interface AssignmentDraft {
  roles: string[];
  extraPermissions: string[];
}

// The union the server resolves: every role's permissions plus the extras. A role the catalog does
// not list contributes nothing, because the panel cannot know what it grants.
export function effectivePermissions(
  roleIds: readonly string[],
  extras: readonly string[],
  roles: readonly CatalogRole[],
): Set<string> {
  const byId = new Map(roles.map((role) => [role.id, role]));
  const effective = new Set<string>();
  for (const id of roleIds) {
    for (const permission of byId.get(id)?.permissions ?? []) effective.add(permission);
  }
  for (const extra of extras) effective.add(extra);
  return effective;
}

const grantsAccessManagement = (role: CatalogRole): boolean => role.permissions.some(isAccessManagement);

// A row the screen cannot save without losing something is managed only by backend:
// - someone who manages access, through a role or an extra (the server rejects the write anyway);
// - someone holding a catalog role backend does not mark assignable. A write replaces the catalog
//   roles with the ones sent, and the server refuses to be sent that role, so any save would drop it.
export function isReadOnly(assignment: RoleAssignment, roles: readonly CatalogRole[]): boolean {
  const roleIds = assignment.roles.map((role) => role.id);
  const notAssignable = new Set(roles.filter((role) => !role.assignable).map((role) => role.id));
  return (
    roleIds.some((id) => notAssignable.has(id)) ||
    [...effectivePermissions(roleIds, assignment.extraPermissions, roles)].some(isAccessManagement)
  );
}

// Backend's assignable list drives the checkboxes, so a new role needs no panel release. A role that
// grants access management is dropped even if backend marks it assignable.
export function assignableRoles(roles: readonly CatalogRole[]): CatalogRole[] {
  return roles.filter((role) => role.assignable && !grantsAccessManagement(role));
}

const isOfferableExtra = (id: string): id is Permission => isKnownPermission(id) && !isAccessManagement(id);

// An extra is offered only when backend lists it and the panel knows what it opens.
export function offerableExtras(permissions: readonly CatalogPermission[]): Permission[] {
  return permissions.map((permission) => permission.id).filter(isOfferableExtra);
}

const managedRoleIds = (roles: readonly CatalogRole[]): Set<string> =>
  new Set(assignableRoles(roles).map((role) => role.id));

// The editable part of a row: the roles the screen can assign and the extras it offers. Fresh
// arrays, so the dialog can mutate its draft without touching the cached row.
export function draftOf(
  assignment: RoleAssignment | null,
  roles: readonly CatalogRole[],
  permissions: readonly CatalogPermission[],
): AssignmentDraft {
  if (assignment === null) return { roles: [], extraPermissions: [] };
  const managed = managedRoleIds(roles);
  const offered = new Set<string>(offerableExtras(permissions));
  return {
    roles: assignment.roles.map((role) => role.id).filter((id) => managed.has(id)),
    extraPermissions: assignment.extraPermissions.filter((id) => offered.has(id)),
  };
}

// What the panel shows read-only: roles the screen cannot assign and extras it does not offer. Both
// use the same rules as draftOf, so every stored role and extra lands in exactly one of the two.
// On an editable row those roles are outside the catalog, which the server keeps on an edit; a
// catalog role the screen does not assign makes the row read-only (isReadOnly). The extras are sent
// back unchanged, because a write's extras are the whole set. Only «Quitar todo» removes them.
export function unmanaged(
  assignment: RoleAssignment,
  roles: readonly CatalogRole[],
  permissions: readonly CatalogPermission[],
): { roles: PanelRole[]; extras: string[] } {
  const managed = managedRoleIds(roles);
  const offered = new Set<string>(offerableExtras(permissions));
  return {
    roles: assignment.roles.filter((role) => !managed.has(role.id)).map((role) => ({ ...role })),
    extras: assignment.extraPermissions.filter((id) => !offered.has(id)),
  };
}

export function isEmptyDraft(draft: AssignmentDraft): boolean {
  return draft.roles.length === 0 && draft.extraPermissions.length === 0;
}
